import { Injectable } from '@angular/core';
import { Study } from '@models';
import { getRomansForPhase, mapRomanToPhase } from '@utils';
import { BehaviorSubject, Observable } from 'rxjs';

type FilterList = Record<string, Set<string>>;

interface FilterEvent {
  source: 'table' | 'chart' | 'kpi';
  field: string;
  value: string;
  action: 'add' | 'remove' | 'clear';
}

interface FilterState {
  appliedFilters: FilterList;
  availableOptions: FilterList;
  filteredData: Study[];
  totalFilterCount: number;
}

interface FilterClientState {
  active: boolean;
  client: string;
}

@Injectable({
  providedIn: 'root'
})
class StudyFilterService {
  private sourceData: Study[] = [];
  private _filterState = new BehaviorSubject<FilterState>({
    appliedFilters: {},
    availableOptions: {},
    filteredData: [],
    totalFilterCount: 0
  });

  private _filterEvents = new BehaviorSubject<FilterEvent | null>(null);
  private _filterClientState = new BehaviorSubject<FilterClientState>({
    active: false,
    client: ''
  });

  // Public observables
  filterState$: Observable<FilterState> = this._filterState.asObservable();
  filterEvents$: Observable<FilterEvent | null> = this._filterEvents.asObservable();
  filterClientState$: Observable<FilterClientState> = this._filterClientState.asObservable();

  /**
   * Initialize the filter service with source data
   */
  initializeFilters(data: Study[]): void {
    this.sourceData = [...data];
    const availableOptions = this.createFilterOptions(data);

    this._filterState.next({
      appliedFilters: {},
      availableOptions,
      filteredData: [...data],
      totalFilterCount: 0
    });
  }

  /**
   * Add a filter from any source (table, chart, KPI)
   */
  addFilter(field: string, value: string, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    const currentState = this._filterState.value;
    const newFilters = { ...currentState.appliedFilters };

    // For studyPhase, normalize chart 'Phase X' selections into the equivalent Roman phases
    if (!newFilters[field]) {
      newFilters[field] = new Set<string>();
    }
    newFilters[field].add(value);

    this.updateFilterState(newFilters, source, field, value, 'add');
  }

  /**
   * Add a filter from switcher client dropdown
   */
  addClientFilter(active: boolean, client: string): void {
    const currentState = this._filterState.value;
    const newFilters = { ...currentState.appliedFilters };
    this._filterClientState.next({ active, client });
    this.updateFilterState(newFilters, 'table', 'clientName', client, 'add');
    sessionStorage.setItem('sponsorSelected', client);
  }
  /**
   * Remove filter from switcher client dropdown
   */
  removeClientFilter(): void {
    const currentState = this._filterState.value;
    const newFilters = { ...currentState.appliedFilters };
    this._filterClientState.next({ active: false, client: '' });
    this.updateFilterState(newFilters, 'table', 'clientName', '', 'remove');
    sessionStorage.removeItem('sponsorSelected');
  }

  /**
   * Remove a specific filter
   */
  removeFilter(field: string, value: string, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    const currentState = this._filterState.value;
    const newFilters = { ...currentState.appliedFilters };

    if (newFilters[field]) {
      // If removing via chart (Phase label), remove all Romans that map to that label
      if (field === 'studyPhase' && source === 'chart') {
        const romans = getRomansForPhase(value);
        // remove the chart label itself too
        if (newFilters[field].has(value)) {
          newFilters[field].delete(value);
        }
        romans.forEach((r) => newFilters[field].delete(r));
      } else if (field === 'studyPhase' && source === 'table') {
        // Removing a roman from the table should also remove any chart labels that map to it
        // delete the roman value itself
        newFilters[field].delete(value);
        // delete any chart labels (like 'Phase 1') that map to this roman
        Array.from(newFilters[field]).forEach((f) => {
          const mappedRomans = getRomansForPhase(f);
          if (mappedRomans.includes(value)) {
            newFilters[field].delete(f);
          }
        });
      } else {
        newFilters[field].delete(value);
      }

      if (newFilters[field].size === 0) {
        delete newFilters[field];
      }
    }

    this.updateFilterState(newFilters, source, field, value, 'remove');
  }

  /**
   * Clear all filters for a specific field
   */
  clearFieldFilters(field: string, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    const currentState = this._filterState.value;
    const newFilters = { ...currentState.appliedFilters };
    delete newFilters[field];

    this.updateFilterState(newFilters, source, field, '', 'clear');
  }

  /**
   * Clear all filters
   */
  clearAllFilters(source: 'table' | 'chart' | 'kpi' = 'table'): void {
    this.updateFilterState({}, source, '', '', 'clear');
  }

  /**
   * Toggle a filter (add if not present, remove if present)
   */
  toggleFilter(field: string, value: string, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    const currentState = this._filterState.value;
    const fieldFilters = currentState.appliedFilters[field];

    // We need to treat equivalent values as the same for studyPhase
    if (field === 'studyPhase') {
      if (source === 'chart') {
        // Chart value is a simple label; consider it present if either the label or any mapped roman is present
        const romans = getRomansForPhase(value);
        const hasLabel = !!fieldFilters && fieldFilters.has(value);
        const hasAnyRoman = !!fieldFilters && romans.some((r) => fieldFilters.has(r));
        if (hasLabel || hasAnyRoman) {
          // remove the chart label and any romans
          this.removeFilter(field, value, source);
        } else {
          this.addFilter(field, value, source);
        }
        return;
      }

      if (source === 'table') {
        // Table value is a roman; toggle directly but also allow chart labels to remove equivalently
        const hasRoman = !!fieldFilters && fieldFilters.has(value);
        const hasChartLabel =
          !!fieldFilters &&
          Array.from(fieldFilters).some((f) => getRomansForPhase(f).includes(value));
        if (hasRoman || hasChartLabel) {
          // remove the roman or the chart label that maps to it
          this.removeFilter(field, value, source);
        } else {
          this.addFilter(field, value, source);
        }
        return;
      }
    }

    if (fieldFilters && fieldFilters.has(value)) {
      this.removeFilter(field, value, source);
    } else {
      this.addFilter(field, value, source);
    }
  }

  /**
   * Set multiple filters at once (useful for chart interactions)
   */
  setFilters(filters: FilterList, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    this.updateFilterState(filters, source, '', '', 'add');
  }

  /**
   * Get current filter state synchronously
   */
  getCurrentState(): FilterState {
    return this._filterState.value;
  }

  /**
   * Check if a specific filter is applied
   */
  isFilterApplied(field: string, value: string): boolean {
    const currentState = this._filterState.value;
    const fieldFilters = currentState.appliedFilters[field];
    if (!fieldFilters) {
      return false;
    }

    if (field === 'studyPhase') {
      // value may be a simple label or roman — consider them equivalent
      // If value is a simple label like 'Phase 1', check if any roman maps to it is present
      const romans = getRomansForPhase(value);
      if (romans.length > 0) {
        return romans.some((r) => fieldFilters.has(r));
      }
      // Otherwise value may be a roman
      return fieldFilters.has(value);
    }

    return fieldFilters.has(value);
  }

  /**
   * Get filtered data for a specific field (useful for chart data)
   */
  getFilteredDataForField(field: keyof Study): any[] {
    const filteredData = this._filterState.value.filteredData;
    return [...new Set(filteredData.map((study) => study[field]).filter(Boolean))];
  }

  /**
   * Apply filters and update state
   */
  private updateFilterState(
    newFilters: FilterList,
    source: 'table' | 'chart' | 'kpi',
    field: string,
    value: string,
    action: 'add' | 'remove' | 'clear'
  ): void {
    const filteredData = this.applyFilters(this.sourceData, newFilters);

    const totalFilterCount = Object.values(newFilters).reduce(
      (sum, set) => sum + (set instanceof Set ? set.size : 0),
      0
    );

    const newState: FilterState = {
      appliedFilters: newFilters,
      availableOptions: this._filterState.value.availableOptions,
      filteredData,
      totalFilterCount
    };

    this._filterState.next(newState);

    // Emit filter event for components to react to
    if (field || action === 'clear') {
      this._filterEvents.next({
        source,
        field,
        value,
        action
      });
    }
  }

  /**
   * Create filter options from source data
   */
  private createFilterOptions(sourceData: Study[]): FilterList {
    const filterOptions: FilterList = {};

    for (const study of sourceData) {
      Object.keys(study).forEach((column) => {
        if (!filterOptions[column]) {
          filterOptions[column] = new Set<string>();
        }

        if (column === 'countryList' || column === 'regionList') {
          const list = study[column as keyof Study] as string[];
          if (Array.isArray(list)) {
            list.forEach((item) => filterOptions[column].add(item));
          }
          return;
        }

        const value = study[column as keyof Study];
        if (value !== null && value !== undefined && value !== '') {
          filterOptions[column].add(value.toString());
        }
      });
    }

    // Remove empty values
    Object.keys(filterOptions).forEach((key) => {
      filterOptions[key].delete('');
    });

    return filterOptions;
  }

  /**
   * Apply filters to data
   */
  private applyFilters(sourceData: Study[], filtersApplied: FilterList): Study[] {
    let filteredData = [...sourceData];

    for (const [field, criteria] of Object.entries(filtersApplied)) {
      if (criteria.size > 0) {
        filteredData = filteredData.filter((item) => {
          const fieldValue = item[field as keyof Study];

          if (field === 'country' && item.countryList) {
            return item.countryList.some((country) => criteria.has(country));
          }

          if (field === 'region' && item.regionList) {
            return item.regionList.some((region) => criteria.has(region));
          }

          if (field === 'studyPhase') {
            const romanValue = fieldValue?.toString() || '';
            const simpleLabel =
              (item as Study).studyPhaseSimpleLabel || mapRomanToPhase(romanValue);

            // If any criterion matches either the roman value or the simple label, include the item
            for (const crit of criteria) {
              if (crit === romanValue || crit === simpleLabel) {
                return true;
              }
            }

            return false;
          }

          return criteria.has(fieldValue?.toString() || '');
        });
      }
    }

    if (this._filterClientState.value.active) {
      filteredData = filteredData.filter(
        (study) => study.clientName === this._filterClientState.value.client
      );
    }

    return filteredData;
  }
}

export type { FilterClientState, FilterEvent, FilterList, FilterState };
export { StudyFilterService };
